import { forwardRef } from "react";
import { inr, summarizeOrders } from "@/lib/format";
import { LEGAL_NAME, WEBSITE } from "@/lib/brand";

const fmtDay = (d) =>
  d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

// Sales summary slip for a chosen date range (defaults to today).
const DaySlip = forwardRef(function DaySlip({ orders, vendor, range }, ref) {
  const now = new Date();
  const from = range?.from || new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const to = range?.to || new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const label = range?.label || "Today";

  const { count, totalItems, sales, rows } = summarizeOrders(orders, from, to);

  const sameDay = from.toDateString() === to.toDateString();
  const rangeText = sameDay ? fmtDay(from) : `${fmtDay(from)}  —  ${fmtDay(to)}`;

  return (
    <div id="dayslip" className="print-doc" ref={ref} style={{ fontFamily: "'JetBrains Mono', monospace", color: "#000" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontWeight: 900, fontSize: "22px" }}>
          {vendor?.shop_name || "1APPONLY"}
        </div>
        <div style={{ fontWeight: 800, fontSize: "17px", marginTop: 2 }}>SALES SUMMARY</div>
        <div style={{ fontSize: "13px", fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: "13px", fontWeight: 600 }}>{rangeText}</div>
        <div style={{ fontSize: "12px", fontWeight: 500 }}>
          Printed {now.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      <div style={{ borderTop: "2px solid #000", margin: "8px 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: "17px" }}>
        <span>Orders</span>
        <span>{count}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: "17px" }}>
        <span>Items sold</span>
        <span>{totalItems}</span>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", margin: "10px 0", fontSize: "16px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #000" }}>
            <th style={{ textAlign: "left", fontWeight: 900, padding: "0 6px 4px 0" }}>Item</th>
            <th style={{ textAlign: "center", fontWeight: 900, padding: "0 6px 4px 6px", whiteSpace: "nowrap" }}>Qty</th>
            <th style={{ textAlign: "right", fontWeight: 900, padding: "0 0 4px 6px", whiteSpace: "nowrap" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} style={{ padding: "8px 0", fontWeight: 500 }}>No sales in this period.</td></tr>
          ) : (
            rows.map(([name, v]) => (
              <tr key={name}>
                <td style={{ textAlign: "left", fontWeight: 700, padding: "4px 6px 4px 0", verticalAlign: "top", lineHeight: 1.25 }}>{name}</td>
                <td style={{ textAlign: "center", fontWeight: 700, padding: "4px 6px", verticalAlign: "top", whiteSpace: "nowrap" }}>{v.qty}</td>
                <td style={{ textAlign: "right", fontWeight: 700, padding: "4px 0 4px 6px", verticalAlign: "top", whiteSpace: "nowrap" }}>{inr(v.amount)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div style={{ borderTop: "2px solid #000", margin: "6px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: "22px" }}>
        <span>TOTAL</span>
        <span>{inr(sales)}</span>
      </div>

      <div style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />
      <div style={{ textAlign: "center", fontSize: "13px", fontWeight: 600 }}>
        Powered by 1APPONLY
        <div style={{ fontSize: "10px", fontWeight: 500, marginTop: 1 }}>{LEGAL_NAME}</div>
        <div style={{ fontSize: "11px", fontWeight: 700, marginTop: 1 }}>{WEBSITE}</div>
      </div>
    </div>
  );
});

export default DaySlip;
